import { Button, H1, H3, Paragraph, Spinner, Text, XStack, YStack } from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { TrackingEvent } from '@mrrmlab/shared-types';
import type { TrackingCandidate } from '@mrrmlab/api-client';
import { useTracking, useTrackingCandidates, useMarkCandidateDoneFromTracking } from '../hooks';

export interface TrackingDetailScreenProps {
  trackingId: string;
}

export function TrackingDetailScreen({ trackingId }: TrackingDetailScreenProps) {
  const query = useTracking(trackingId);
  const candidatesQuery = useTrackingCandidates(trackingId);
  const markDone = useMarkCandidateDoneFromTracking(trackingId);

  if (query.isPending) {
    return (
      <Screen>
        <YStack flex={1} alignItems="center" justifyContent="center" padding="$6">
          <Spinner size="large" />
        </YStack>
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen>
        <Paragraph color="$red10">
          Paket konnte nicht geladen werden: {query.error.message}
        </Paragraph>
      </Screen>
    );
  }

  const t = query.data;
  const carrierLabel = (t.carrier ?? 'Unbekannter Carrier').toUpperCase();
  const statusLabel = t.status ?? 'Pending';
  const lastUpdate = t.lastStatusAt ? formatDateTime(t.lastStatusAt) : null;

  return (
    <Screen>
      <YStack gap="$2">
        <Text fontSize="$2" opacity={0.6} letterSpacing={0.5}>
          {carrierLabel}
        </Text>
        <H1>{t.trackingNumber}</H1>
        {t.orderInfo ? (
          <Text fontSize="$4" opacity={0.8}>
            {t.orderInfo.shopName ?? '—'}
            {t.orderInfo.orderNumber ? ` · Bestell-Nr. ${t.orderInfo.orderNumber}` : ''}
          </Text>
        ) : null}
      </YStack>

      <XStack
        alignItems="center"
        gap="$3"
        paddingVertical="$3"
        paddingHorizontal="$4"
        borderRadius="$4"
        backgroundColor="$color3"
      >
        <YStack flex={1} gap="$1">
          <Text fontSize="$2" opacity={0.7}>
            Aktueller Status
          </Text>
          <Paragraph fontSize="$6" fontWeight="700">
            {statusLabel}
          </Paragraph>
          {t.subStatus ? (
            <Text fontSize="$2" opacity={0.7}>
              {t.subStatus}
            </Text>
          ) : null}
        </YStack>
      </XStack>

      <XStack
        alignItems="flex-start"
        gap="$3"
        paddingVertical="$3"
        paddingHorizontal="$4"
        borderRadius="$3"
        borderWidth={1}
        borderColor="$blue6"
        backgroundColor="$blue2"
      >
        <Text fontSize="$5">📬</Text>
        <YStack flex={1} gap="$1">
          <Text fontSize="$2" fontWeight="700" opacity={0.85}>
            Auto-Todo aktiv
          </Text>
          <Paragraph fontSize="$3" opacity={0.85}>
            Bei wichtigen Status-Änderungen (abholbereit, zugestellt, Problem) wird automatisch ein Todo im Tagesplan erstellt.
          </Paragraph>
        </YStack>
      </XStack>

      {(candidatesQuery.data && candidatesQuery.data.length > 0) && (
        <YStack gap="$2">
          <H3>Todos</H3>
          {candidatesQuery.data.map((c) => (
            <CandidateRow
              key={c.id}
              candidate={c}
              onMarkDone={() => markDone.mutate(c.id)}
            />
          ))}
        </YStack>
      )}

      <YStack gap="$3">
        <H3>Verlauf</H3>
        {t.events.length > 0 ? (
          sortEventsDesc(t.events).map((event, index) => (
            <TimelineEntry
              key={event.id}
              when={formatDateTime(event.occurredAt)}
              label={event.description}
              sublabel={event.location}
              current={index === 0}
            />
          ))
        ) : (
          <TimelineEntry
            when={lastUpdate ?? formatDateTime(t.createdAt)}
            label={statusLabel}
            sublabel={t.subStatus}
            current
          />
        )}
        <TimelineEntry
          when={formatDateTime(t.createdAt)}
          label="Erfasst"
          sublabel={`Tracking-Nummer registriert`}
        />
      </YStack>
    </Screen>
  );
}

function sortEventsDesc(events: TrackingEvent[]): TrackingEvent[] {
  return [...events].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

interface TimelineEntryProps {
  when: string;
  label: string;
  sublabel?: string | null;
  current?: boolean;
}

function TimelineEntry({ when, label, sublabel, current }: TimelineEntryProps) {
  return (
    <XStack gap="$3" alignItems="flex-start">
      <YStack
        width={12}
        height={12}
        borderRadius={6}
        marginTop="$1"
        backgroundColor={current ? '$green9' : '$color6'}
      />
      <YStack flex={1} gap="$1">
        <Text fontSize="$2" opacity={0.6}>
          {when}
        </Text>
        <Paragraph fontSize="$5" fontWeight={current ? '600' : '400'}>
          {label}
        </Paragraph>
        {sublabel ? (
          <Text fontSize="$2" opacity={0.7}>
            {sublabel}
          </Text>
        ) : null}
      </YStack>
    </XStack>
  );
}

const PRIORITY_COLOR: Record<TrackingCandidate['priority'], string> = {
  low: '$color7',
  normal: '$color9',
  high: '$red9',
};

interface CandidateRowProps {
  candidate: TrackingCandidate;
  onMarkDone: () => void;
}

function CandidateRow({ candidate, onMarkDone }: CandidateRowProps) {
  const isDone = candidate.lifecycleState === 'done';
  return (
    <XStack
      alignItems="center"
      gap="$3"
      paddingVertical="$3"
      paddingHorizontal="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      opacity={isDone ? 0.5 : 1}
    >
      <YStack flex={1} gap="$1">
        <Text
          fontSize="$4"
          fontWeight="600"
          textDecorationLine={isDone ? 'line-through' : 'none'}
          color={PRIORITY_COLOR[candidate.priority]}
        >
          {candidate.title}
        </Text>
        <Text fontSize="$2" opacity={0.6}>
          {isDone ? 'Erledigt' : 'Offen'}
        </Text>
      </YStack>
      {!isDone && (
        <Button size="$2" onPress={onMarkDone}>
          Erledigt
        </Button>
      )}
    </XStack>
  );
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
